import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  ArrowLeftRight,
  CalendarClock,
  Repeat,
  BarChart3,
  Tag,
  LogOut,
  TrendingUp,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const navItems = [
  { to: "/", label: "Özet", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/islemler", label: "Gelir & Gider", icon: ArrowLeftRight, testid: "nav-transactions" },
  { to: "/yaklasan", label: "Tahsilat & Ödeme", icon: CalendarClock, testid: "nav-upcoming" },
  { to: "/abonelikler", label: "Abonelikler", icon: Repeat, testid: "nav-recurring" },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3, testid: "nav-reports" },
  { to: "/kategoriler", label: "Kategoriler", icon: Tag, testid: "nav-categories" },
];

const currencies = ["TRY", "USD", "EUR"];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { base, setBase } = useCurrency();
  useLocation();

  const initials = (user?.name || "F")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 grain-bg">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0F1522] border-r border-white/5 hidden md:flex flex-col z-30">
        <div className="px-6 pt-7 pb-5">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-extrabold text-lg tracking-tight">Finakış</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Finans Takip</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.25em] text-slate-500">Menü</div>
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-indigo-500/10 text-indigo-300 border border-indigo-500/20"
                    : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-all"
              >
                <Avatar className="w-9 h-9 border border-white/10">
                  <AvatarFallback className="bg-indigo-500/20 text-indigo-200 text-xs font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#131924] border-white/10">
              <DropdownMenuLabel>{user?.business_name || "Hesap"}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-rose-400 focus:text-rose-300 focus:bg-rose-500/10">
                <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main area */}
      <div className="md:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-20 bg-[#0B0F17]/85 backdrop-blur-xl border-b border-white/5">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Workspace</div>
              <div className="font-display text-xl font-bold tracking-tight">
                {user?.business_name || "Finakış"}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center bg-[#131924] border border-white/10 rounded-lg p-1">
                {currencies.map((c) => (
                  <button
                    key={c}
                    data-testid={`currency-toggle-${c.toLowerCase()}`}
                    onClick={() => setBase(c)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                      base === c
                        ? "bg-indigo-500 text-white"
                        : "text-slate-400 hover:text-slate-100"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* Mobile nav */}
          <nav className="md:hidden flex overflow-x-auto gap-1 px-3 pb-3">
            {navItems.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={`m-${testid}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${
                    isActive
                      ? "bg-indigo-500/10 text-indigo-300 border border-indigo-500/20"
                      : "text-slate-400 bg-[#131924] border border-white/5"
                  }`
                }
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </NavLink>
            ))}
          </nav>
        </header>

        <main className="p-6 max-w-[1600px] mx-auto">{children}</main>
      </div>
    </div>
  );
}

export { Button };
